import { Box, Card, Link, Stack, Typography } from '@mui/material';
import { useEffect, type ReactNode } from 'react';
import BrandLogo from '@/components/common/BrandLogo';
import ProductLogo from '@/components/common/ProductLogo';
import { COMPANY_LEGAL_NAME, COMPANY_NAME, PRODUCT_BRAND } from '@/config/productBrand';
import { FALLBACK_BRAND_NAME, useApplyBranding, usePublicBranding } from '@/hooks/usePublicBranding';

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** GoERP's own screens (the Platform Console): always GoERP's name and logo, never a company's. */
  productBranded?: boolean;
}

/** Split-screen auth layout: brand panel + form card. Themed with public tenant branding. */
export default function AuthShell({ title, subtitle, children, footer, productBranded = false }: Props) {
  const { data: branding } = usePublicBranding();
  // Tenant title + favicon on a company's auth screens; the Platform Console keeps GoERP's.
  useApplyBranding(!productBranded);
  useEffect(() => {
    if (productBranded) document.title = `${title} — ${COMPANY_NAME}`;
  }, [productBranded, title]);
  const companyName = productBranded ? COMPANY_NAME : branding?.companyName?.trim() || FALLBACK_BRAND_NAME;
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { md: 5, lg: 6 },
          gap: 4,
          color: '#fff',
          background:
            'radial-gradient(1200px 600px at 20% 20%, #6366f1 0%, #4338ca 40%, #1e1b4b 100%)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {productBranded ? <ProductLogo width={40} variant="icon" /> : <BrandLogo size={40} />}
          <Typography variant="h6" fontWeight={800}>
            {companyName}
          </Typography>
        </Stack>
        <Box>
          <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5, lineHeight: 1.2 }}>
            Run your travel business,
            <br /> beautifully.
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 460 }}>
            Capture leads, schedule follow-ups, manage destinations and your whole team — all in one
            modern travel management platform.
          </Typography>
        </Box>
        {/* Footer: copyright and attribution as one block, so the space-between layout keeps them together. */}
        <Stack spacing={0.75}>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            © {new Date().getFullYear()} {productBranded ? COMPANY_LEGAL_NAME : companyName}. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.8 }}>
            <ProductLogo width={26} variant="icon" />
            <Typography variant="caption">
              Powered by {PRODUCT_BRAND.poweredByYear}{' '}
              <Link href={PRODUCT_BRAND.website} target="_blank" rel="noreferrer" color="inherit" underline="hover">
                {PRODUCT_BRAND.legalName}
              </Link>
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0,
          p: { xs: 2, sm: 4 },
          bgcolor: 'background.default',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 420, p: { xs: 2.5, sm: 4 } }}>
          {/* Icon, not the wordmark: this card is white and the wordmark's text is white too. */}
          <ProductLogo width={40} variant="icon" sx={{ mb: 1.5, display: { xs: 'block', md: 'none' } }} />
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ fontSize: { xs: '1.375rem', sm: '1.625rem' }, lineHeight: 1.25, mb: 0.75 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {subtitle}
            </Typography>
          )}
          {children}
          {footer && <Box sx={{ mt: 2.5, textAlign: 'center' }}>{footer}</Box>}

          {/* Attribution on the form side too — on mobile the brand panel is hidden entirely. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: 'block', md: 'none' }, mt: 2.5, textAlign: 'center' }}
          >
            {PRODUCT_BRAND.poweredBy}
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}
